// Web Audio API sound manager — fully self-contained, no external files.
// All tones are synthesized. Respects a global mute. Lazily creates the
// AudioContext on first user gesture (browser autoplay policy).

class SoundManager {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.muted = false;
    this.musicNodes = null;
    this.musicWorld = null;
  }

  _ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.5;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.5;
  }

  _blip(freq, dur, type = "sine", vol = 0.2, when = 0) {
    if (this.muted) return;
    this._ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime + when;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  _sweep(f1, f2, dur, vol = 0.15) {
    if (this.muted) return;
    this._ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f1, t);
    o.frequency.exponentialRampToValueAtTime(f2, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  playClick() {
    this._blip(420, 0.08, "triangle", 0.1);
  }

  // hover preview per world
  playHover(world) {
    if (world === "cafe") {
      this._blip(880, 0.25, "sine", 0.12);
      this._blip(1320, 0.3, "sine", 0.07, 0.05);
    } else if (world === "academy") {
      [1046, 1318, 1568].forEach((f, i) => this._blip(f, 0.18, "triangle", 0.09, i * 0.06));
    } else {
      this._blip(110, 0.45, "sawtooth", 0.12);
      this._blip(165, 0.45, "sine", 0.05, 0.02);
    }
  }

  playPortal(world) {
    const base = world === "academy" ? 330 : world === "alley" ? 120 : 262;
    this._blip(base, 0.6, "sine", 0.2);
    this._blip(base * 2, 0.6, "triangle", 0.1, 0.05);
    this._sweep(base, base * 3.2, 0.7, 0.16);
  }

  playUnlock() {
    [659, 830, 988, 1319].forEach((f, i) => this._blip(f, 0.3, "triangle", 0.13, i * 0.08));
  }

  playAchievement() {
    [523, 659, 784, 1047].forEach((f, i) => this._blip(f, 0.35, "square", 0.09, i * 0.09));
  }

  playGift() {
    [784, 1047, 1319].forEach((f, i) => this._blip(f, 0.25, "sine", 0.12, i * 0.07));
  }

  playSuccess() {
    [523, 784, 1047].forEach((f, i) => this._blip(f, 0.3, "triangle", 0.12, i * 0.1));
  }

  playError() {
    this._blip(180, 0.25, "sawtooth", 0.1);
    this._blip(120, 0.3, "sawtooth", 0.1, 0.08);
  }

  playBurst(world) {
    const base = world === "academy" ? 880 : world === "alley" ? 220 : 660;
    for (let i = 0; i < 6; i++) {
      this._blip(base * (1 + Math.random()), 0.22, "triangle", 0.05, i * 0.02);
    }
  }

  // ambient drone music per world
  startMusic(world) {
    this.stopMusic();
    this._ensure();
    if (!this.ctx) return;
    const ctx = this.ctx;
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.0001, ctx.currentTime);
    out.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 1.6);
    out.connect(this.master);

    const scales = {
      cafe: [196, 262, 330, 392],
      academy: [220, 294, 370, 440],
      alley: [98, 131, 165, 196],
      studio: [262, 330, 415, 523],
    };
    const notes = scales[world] || scales.cafe;
    const oscs = [];
    notes.slice(0, 3).forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = world === "alley" ? "sawtooth" : "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = 0.4 - i * 0.08;
      // gentle detune shimmer
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.07 + i * 0.03;
      lfoGain.gain.value = 1.5;
      lfo.connect(lfoGain);
      lfoGain.connect(o.detune);
      o.connect(g);
      g.connect(out);
      o.start();
      lfo.start();
      oscs.push(o, lfo);
    });
    this.musicNodes = { out, oscs };
    this.musicWorld = world;
  }

  stopMusic() {
    if (this.musicNodes && this.ctx) {
      const { out, oscs } = this.musicNodes;
      try {
        out.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.6);
        oscs.forEach((o) => o.stop(this.ctx.currentTime + 0.7));
      } catch (e) {
        /* noop */
      }
    }
    this.musicNodes = null;
    this.musicWorld = null;
  }
}

export const sound = new SoundManager();
